/* eslint-disable no-prototype-builtins */
import {
  AccountContactPreferencesDocument,
  AccountRelationshipFormInput,
  AccountRelationshipType,
  AccountType,
  AccountWithTypeFragment,
  MinimalAccountRelationshipsDocument,
  RelationshipType,
  useAccountChildrenQuery,
  useAccountParentsQuery,
  useClientTypesQuery,
  useCreateOrUpdateAccountRelationshipMutation,
} from 'graphql/bindings/AdminBindings'
import { AccountTypesField } from 'components/shared/dropdowns'
import {
  Box,
  DateField,
  FlagField,
  FormModal,
  HelperText,
  SpinnerButton,
  TextField,
  Theme,
  ValueAutocompleteField,
  makeStyles,
} from '@forgeglobal/design-framework'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { displayRelationshipType, isIntroduction } from 'util/RelationshipUtils'
import { getOperationName } from 'apollo-link'
import HighlightedSection from 'components/shared/HighlightedSection'
import React, { Fragment, useEffect, useMemo } from 'react'
import compact from 'lodash/compact'
import moment, { Moment } from 'moment'

interface AccountOption {
  label: string
  value: AccountWithTypeFragment
}

export interface RelationshipShape {
  id?: string
  account?: AccountOption
  relationship?: RelationshipType
  start_date?: Moment | null | undefined
  title?: string | null | undefined
  type?: AccountType | null | undefined
  is_intermediary?: boolean
  client_type_id?: string
  source_parent_org_id?: string | null
  source_org_employee_id?: string | null
  dest_parent_org_id?: string | null
  dest_org_employee_id?: string | null
}

interface CurrentAccountData {
  currentAccountType?: AccountType
  currentAccountName: string
  currentAccountId: string
  start_date?: Moment | null | undefined
}

const useStyles = makeStyles((theme: Theme) => ({
  disabled: {
    fontWeight: 'normal !important' as any,
    opacity: 0.5,
    marginTop: theme.spacing(0.5),
  },
}))

type FormShape = CurrentAccountData & RelationshipShape

const getRelationshipOptions: (accountType: string) => { label: string; value: string }[] = (accountType: string) => {
  if (accountType === 'indiv') {
    return [
      RelationshipType.IsEmployedBy,
      RelationshipType.Employs,
      RelationshipType.IsAffiliatedWith,
      RelationshipType.Introduced,
      RelationshipType.WasIntroducedBy,
    ].map(relationshipType => ({ label: displayRelationshipType(relationshipType), value: relationshipType }))
  }
  return [
    RelationshipType.IsParentCompanyOf,
    RelationshipType.IsSubOrgOf,
    RelationshipType.IsAffiliatedWith,
    RelationshipType.Introduced,
    RelationshipType.WasIntroducedBy,
  ].map(relationshipType => ({ label: displayRelationshipType(relationshipType), value: relationshipType }))
}

const ClientTypeField: React.FC<{ accountType: string }> = ({ accountType }) => {
  const { data } = useClientTypesQuery({ variables: { accountType: accountType } })
  const clientTypeOptions =
    data?.client_types
      ?.map(ctype => ({ label: ctype.name, value: ctype.id }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? []

  return <ValueAutocompleteField name="client_type_id" label="Client Type" options={clientTypeOptions} />
}

interface ParentOrgFieldProps {
  accountId: string
  accountName: string
  fieldName: string
  parentOrgId: string | null | undefined
  isIntroducer: boolean
  isPristine: boolean
  formChange: any
}

const ParentOrgField: React.FC<ParentOrgFieldProps> = ({
  accountId,
  accountName,
  fieldName,
  parentOrgId,
  isIntroducer,
  formChange,
  isPristine,
}) => {
  const accountParentsData = useAccountParentsQuery({ variables: { accountId }, skip: !accountId }).data
  const parentOrgs = useMemo(() => {
    return (accountParentsData?.account?.parents ?? [])
      .map(a => a.parent_account)
      .filter(a => a.type === 'organization')
      .map(a => ({ label: a.name, value: a.id }))
  }, [accountParentsData])

  useEffect(() => {
    if (!isPristine) {
      if (parentOrgs.length > 0) {
        formChange(fieldName, parentOrgs[0].value)
      }
    }
    // eslint-disable-next-line
  }, [parentOrgs, formChange])

  let helperText: string | undefined
  if (!parentOrgId && parentOrgs.length === 0 && isIntroducer) {
    helperText = `${accountName} does not have an associated organization account in the system. If ${accountName} works for an organization, please create that relationship before adding this introduction`
  } else if (!parentOrgId && parentOrgs.length > 0) {
    helperText = `${accountName} works for an organization. If this introduction was made on behalf of ${accountName}'s organization, be sure to select it.`
  }

  return (
    <Fragment>
      {parentOrgs.length > 0 && (
        <ValueAutocompleteField name={fieldName} label="Who works at org" options={parentOrgs} />
      )}
      {helperText && <HelperText text={helperText} />}
    </Fragment>
  )
}
interface OrgEmployeeFieldProps {
  fieldName: string
  accountId: string
}

const OrgEmployeeField: React.FC<OrgEmployeeFieldProps> = ({ fieldName, accountId }) => {
  const { data } = useAccountChildrenQuery({ variables: { accountId } })
  const childOptions = useMemo(() => {
    return (data?.account?.relationships ?? [])
      .filter(r => r.parent_to_child_relationship === AccountRelationshipType.Employs)
      .map(a => a.child_account)
      .filter(a => a.type === AccountType.Individual)
      .map(a => ({ label: a.name, value: a.id }))
  }, [data])

  return (
    <Fragment>
      {childOptions.length ? (
        <ValueAutocompleteField name={fieldName} label="Via" options={childOptions} />
      ) : (
        <HelperText text={'This org has no child relationships'} />
      )}
    </Fragment>
  )
}

const NewRelationshipForm: React.FC<FormRenderProps<FormShape> & { highlightIntermediary: boolean | undefined }> = ({
  handleSubmit,
  form,
  values,
  valid,
  pristine,
  highlightIntermediary: highlightIntermediarySection,
}) => {
  const issuerOrOrg = [AccountType.Issuer, AccountType.Organization]
  const classes = useStyles()
  const relationshipType =
    values.currentAccountType &&
    values.type &&
    issuerOrOrg.includes(values.currentAccountType) &&
    issuerOrOrg.includes(values.type)
      ? 'org'
      : 'indiv'
  const formChange = form.change // This is hack to get around exhaustive-deps, not binding and it still works [for now]
  const destAccount = values.account?.value
  const destAccountType: AccountType | undefined = values.type ?? destAccount?.type

  useEffect(() => {
    if (!pristine) {
      formChange('type', destAccount?.type ?? '')
      formChange('title', undefined)
    }
    // eslint-disable-next-line
  }, [values.account, formChange])

  const formIsIntroduction = values.relationship && isIntroduction(values.relationship)

  const isSourceAccountIntermediated = ['is_employed_by', 'is_affiliated_with', 'introduced'].includes(
    values.relationship || ''
  )
  const sourceAccountName = values.currentAccountName
  const isNewAccount = !destAccount && values.account
  const destAccountName = destAccount?.name ?? ''
  let [intermediaryAccountName, intermediatedAccountName]: Array<string | undefined> = [undefined, undefined]
  if (isSourceAccountIntermediated)
    [intermediaryAccountName, intermediatedAccountName] = [destAccountName, sourceAccountName]
  else [intermediaryAccountName, intermediatedAccountName] = [sourceAccountName, destAccountName]
  const intermediaryLabel = `Is ${intermediaryAccountName} an intermediary for ${intermediatedAccountName}?`

  return (
    <form onSubmit={handleSubmit}>
      <div data-cypress="relationship-form-name">
        <AccountTypesField name="account" label="Name" freeSolo />
        {formIsIntroduction &&
          destAccount &&
          values.relationship &&
          !isNewAccount &&
          (destAccountType === AccountType.Individual ? (
            <ParentOrgField
              accountId={destAccount.id}
              accountName={destAccount.name}
              fieldName="dest_parent_org_id"
              parentOrgId={values.dest_parent_org_id}
              isIntroducer={values.relationship === RelationshipType.Introduced}
              formChange={formChange}
              isPristine={pristine}
            />
          ) : (
            <OrgEmployeeField fieldName="dest_org_employee_id" accountId={destAccount.id} />
          ))}
      </div>
      {isNewAccount && (
        <Fragment>
          <ValueAutocompleteField
            name="type"
            options={[
              { label: 'Organization', value: 'organization' },
              { label: 'Individual', value: 'individual' },
            ]}
            label="Type"
          />
          {values.type && <ClientTypeField accountType={values.type} />}
        </Fragment>
      )}
      {(destAccount || values.type) && (
        <Fragment>
          <ValueAutocompleteField
            name="relationship"
            options={getRelationshipOptions(relationshipType)}
            label="Relationship type"
          />
          <Field
            name="currentAccountName"
            component={TextField}
            InputLabelProps={{ classes: { disabled: classes.disabled } }}
            label="This account"
            disabled
            fullWidth
          />
          {formIsIntroduction &&
            destAccount &&
            (values.currentAccountType === AccountType.Individual ? (
              <ParentOrgField
                accountId={values.currentAccountId}
                accountName={values.currentAccountName}
                fieldName="source_parent_org_id"
                parentOrgId={values.source_parent_org_id}
                isIntroducer={values.relationship === RelationshipType.WasIntroducedBy}
                formChange={formChange}
                isPristine={pristine}
              />
            ) : (
              <OrgEmployeeField fieldName="source_org_employee_id" accountId={values.currentAccountId} />
            ))}
        </Fragment>
      )}
      {relationshipType === 'indiv' && !!values.relationship && (
        <Fragment>
          {!formIsIntroduction && <Field name="title" component={TextField} label="As a(n)" fullWidth />}
          {formIsIntroduction && <DateField name="start_date" label="Introduced on" />}
          {values.currentAccountType !== 'issuer' && (
            <HighlightedSection highlighted={highlightIntermediarySection || false} ref={null}>
              <Field name="is_intermediary" label={intermediaryLabel} component={FlagField} fullWidth />
              {values.is_intermediary && (
                <HelperText
                  text={`${intermediatedAccountName} will be added to the 'Do Not Contact' list. 
              Contact with this acount will be recommended through ${intermediaryAccountName}`}
                />
              )}
            </HighlightedSection>
          )}
        </Fragment>
      )}
      <Box mt={2}>
        <SpinnerButton
          variant="contained"
          loading={false}
          caption="Submit"
          color="primary"
          type="submit"
          disabled={!valid}
          fullWidth
        />
      </Box>
    </form>
  )
}

const validateForm = (values: FormShape) => {
  const keys = Object.keys(values)
  const requiredKeys = ['account', 'relationship', 'type']
  const errors = requiredKeys.reduceRight((acc, key) => {
    if (keys.includes(key) && (values as any)[key] !== null && (values as any)[key] !== '' && !!(values as any)[key]) {
      return acc
    }
    acc[key] = `${key} required`
    return acc
  }, {} as { [key: string]: string })
  return errors
}

export const RelationshipsModal: React.FC<{
  hideModal: () => void
  accountId: string
  accountName: string
  accountType?: AccountType
  accountClientTypeId?: string
  existingRelationship?: RelationshipShape
  editing?: boolean
  highlightIntermediarySection?: boolean
}> = ({
  hideModal,
  accountId,
  accountName,
  accountType,
  existingRelationship,
  editing,
  highlightIntermediarySection: highlightIntermediary,
}) => {
  const [createOrUpdateRelationship] = useCreateOrUpdateAccountRelationshipMutation({
    refetchQueries: compact([
      getOperationName(MinimalAccountRelationshipsDocument),
      getOperationName(AccountContactPreferencesDocument),
    ]),
    onCompleted: hideModal,
  })

  const onSubmit = (values: FormShape) => {
    const account = values?.account as any
    const isNewAccount = !account.hasOwnProperty('label')

    const destAccountId = values.dest_org_employee_id ?? account?.value?.id
    const destParentOrgId = values.dest_org_employee_id ? account?.value?.id : values.dest_parent_org_id

    const sourceAccountId = values.source_org_employee_id ?? accountId
    const sourceParentOrgId = values.source_org_employee_id ? accountId : values.source_parent_org_id

    createOrUpdateRelationship({
      variables: {
        form: {
          id: existingRelationship?.id,
          sourceAccountId,
          sourceParentOrgId,
          destAccountId,
          destAccountName: isNewAccount ? account : account?.value?.label ?? '',
          destParentOrgId,
          destAccountType: values?.type,
          destAccountClientTypeId: values?.client_type_id,
          relationship: values?.relationship,
          startDate: values?.start_date ? moment(values?.start_date).format('YYYY-MM-DD') : null,
          title: values?.title,
          isIntermediary: values?.is_intermediary ?? false,
        } as AccountRelationshipFormInput,
      },
    })
  }

  const baseInitialValues: CurrentAccountData = {
    currentAccountType: accountType,
    currentAccountName: accountName,
    currentAccountId: accountId,
    start_date: moment(),
  }

  const initialValues: FormShape = editing
    ? {
        ...baseInitialValues,
        ...existingRelationship,
      }
    : baseInitialValues
  return (
    <FormModal open hideModal={hideModal} title={`${editing ? 'Edit' : 'Add'} Relationship`}>
      <Form<FormShape>
        render={props => <NewRelationshipForm highlightIntermediary={highlightIntermediary} {...props} />}
        onSubmit={onSubmit}
        validate={validateForm}
        initialValues={{
          ...initialValues,
        }}
      />
    </FormModal>
  )
}
